/**
 * Global barcode-lookup scan box (SPEC screen 5) — the canonical
 * scan-to-navigate pattern: capture (shared scanner wedge) → validate
 * (symbology check digits, non-blocking) → resolve (GET /variants?barcode=,
 * 0-or-1 contract) → target | miss.
 *
 * The production Catalog route registers this resolver in the authenticated
 * shell's topbar slot. Inline mode remains available for scanner-first floor
 * screens and isolated component tests.
 */
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ScanBarcode } from "lucide-react";
import { Button, Input, Spinner, toast } from "@erp/ui";

import { useRegisterScanBox } from "@/app-shell/scan-slot";

// Shared scanner utility — imported RELATIVELY per the workspace contract.
import { useScannerInput } from "../../shared/scanner/src/react";
import { isValidEan13, isValidGtin14, isValidUpcA } from "../../shared/scanner/src/symbology";

import { lookupVariantsByBarcode, type Variant } from "../api";

/**
 * Non-blocking check-digit verdict: an all-digit value of GTIN length whose
 * GS1 check digit fails is *probably* a mis-scan, but CODE128 legitimately
 * carries free-form digit runs, so lookup still proceeds (SPEC).
 */
export function checkDigitWarning(value: string): boolean {
  if (!/^\d+$/.test(value)) return false;
  if (value.length === 12) return !isValidUpcA(value);
  if (value.length === 13) return !isValidEan13(value);
  if (value.length === 14) return !isValidGtin14(value);
  return false;
}

export interface BarcodeScanBoxProps {
  /** Exactly one variant matched — navigate to its product editor. */
  onHit: (variant: Variant) => void;
  /** True when the user holds `catalog.product.update` (register shortcut). */
  canRegisterBarcode?: boolean;
  /** "Register barcode" shortcut on a miss. */
  onRegister?: (barcode: string) => void;
  className?: string;
  /** Autofocus for inline floor screens; the topbar slot manages its own focus. */
  autoFocus?: boolean;
  /** Topbar registration in the Catalog shell; inline field on floor screens. */
  placement?: "inline" | "topbar";
}

function InlineScannerCapture({ onScan }: { onScan: (value: string) => void }) {
  useScannerInput({
    onScan: (scan) => onScan(scan.value),
  });
  return null;
}

function CatalogTopbarRegistration({
  onScan,
  disabled,
}: {
  onScan: (value: string) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  useRegisterScanBox({
    onScan,
    disabled,
    label: t("catalog.scan.label", "Scan barcode"),
    placeholder: t("catalog.scan.placeholder", "Scan barcode"),
    hint: t("catalog.scan.hint", "F2 focuses the global Catalog barcode resolver."),
  });
  return null;
}

export function BarcodeScanBox({
  onHit,
  canRegisterBarcode = false,
  onRegister,
  className,
  autoFocus = false,
  placement = "inline",
}: BarcodeScanBoxProps) {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [miss, setMiss] = useState<string | null>(null);
  const [warning, setWarning] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const busyRef = useRef(false);

  const resolve = async (code: string) => {
    const trimmed = code.trim();
    if (trimmed === "" || busyRef.current) return;
    setWarning(checkDigitWarning(trimmed));
    setMiss(null);
    busyRef.current = true;
    setBusy(true);
    try {
      const hits = await lookupVariantsByBarcode(trimmed);
      const only = hits.length === 1 ? hits[0] : undefined;
      if (only !== undefined) {
        setValue("");
        setWarning(false);
        onHit(only);
      } else if (hits.length === 0) {
        setMiss(trimmed);
      } else {
        // The endpoint guarantees 0-or-1; >1 is a contract violation.
        toast({
          variant: "destructive",
          title: t("catalog.scan.multipleMatches", "Barcode matched more than one variant"),
        });
      }
    } catch {
      // Network/API failure: keep the value for re-scan (SPEC error state).
      toast({ variant: "destructive", title: t("errors.network") });
    } finally {
      busyRef.current = false;
      setBusy(false);
      inputRef.current?.focus();
    }
  };

  return (
    <div className={className}>
      {placement === "inline" ? (
        <InlineScannerCapture
          onScan={(scanValue) => {
            setValue(scanValue);
            void resolve(scanValue);
          }}
        />
      ) : (
        <CatalogTopbarRegistration
          onScan={(scanValue) => void resolve(scanValue)}
          disabled={busy}
        />
      )}
      {placement === "inline" ? (
        <div className="relative flex items-center gap-2">
          <ScanBarcode aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
          <Input
            ref={inputRef}
            // Codes are Latin digit runs — never bidi-reordered inside RTL.
            dir="ltr"
            autoFocus={autoFocus}
            value={value}
            disabled={busy}
            aria-label={t("catalog.scan.label", "Scan barcode")}
            placeholder={t("catalog.scan.placeholder", "Scan barcode")}
            onChange={(event) => {
              setValue(event.target.value);
              setMiss(null);
              setWarning(false);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                void resolve(value);
              }
            }}
            className="w-56 font-mono"
          />
          {busy ? <Spinner className="absolute end-2 size-4" /> : null}
        </div>
      ) : busy ? (
        <Spinner label={t("app.loading")} className="size-4" />
      ) : null}
      {warning ? (
        <p role="status" className="mt-1 text-xs text-warning-foreground">
          {t("catalog.scan.checkDigitWarning", "Check digit looks wrong — looked it up anyway.")}
        </p>
      ) : null}
      {miss !== null ? (
        <p role="status" className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
          <span>
            {t("catalog.scan.noMatch", "No variant for barcode {{barcode}}", { barcode: miss })}
          </span>
          {canRegisterBarcode && onRegister ? (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0 text-xs"
              onClick={() => onRegister(miss)}
            >
              {t("catalog.scan.registerBarcode", "Register barcode")}
            </Button>
          ) : null}
        </p>
      ) : null}
    </div>
  );
}
